import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Input, Button, Dropdown } from "antd";
import type { MenuProps } from "antd";
import { useDebounce } from "@react-hook/debounce";
import { HiSearch } from "react-icons/hi";
import { ApolloError } from "@apollo/client";

import ScrollToTopButton from "components/scroll-to-top-button/scroll-to-top-button";
import HandoversListItem from "components/handovers-list-item/handovers-list-item";

import { Handover } from "graphql/generated/graphql";
import { useGetHandoverByOrganizationIdsQuery } from "graphql/queries/get-handovers-by-organization-ids.generated";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useGuideFiltersStore } from "state/zustand-stores/guide-filters-store";

import routeNames from "constants/route-names";
import { SEARCH_DEBOUNCE_DELAY_MS } from "constants/constants";
import { HandoverStatus, OrganizationType } from "constants/field-values";
import { createLabelForFilters, voidFnc } from "utils/helpers";

interface GroupedHandovers {
	forReview: Handover[];
	open: Handover[];
	completed: Handover[];
}

const CUSTOMER_FOR_REVIEW_STATUSES = new Set<string>([HandoverStatus.PENDING_CUSTOMER]);
const CUSTOMER_OPEN_STATUSES = new Set<string>([HandoverStatus.PENDING_VENDOR]);

const VENDOR_FOR_REVIEW_STATUSES = new Set<string>([HandoverStatus.PENDING_VENDOR]);
const VENDOR_OPEN_STATUSES = new Set<string>([HandoverStatus.VENDOR_DRAFT, HandoverStatus.PENDING_CUSTOMER]);

const COMPLETED_STATUSES = new Set<string>([HandoverStatus.PUBLISHED, HandoverStatus.CANCELLED]);

const HandoverStatusValues = Object.values(HandoverStatus);

const matchesSearch = (handover: Handover, term: string): boolean => {
	if (!term) return true;
	const lower = term.trim().toLowerCase();
	return (
		handover.project_name.trim().toLowerCase().includes(lower) ||
		(handover.vendor_organization?.name ?? "").trim().toLowerCase().includes(lower) ||
		(handover.property?.name ?? "").trim().toLowerCase().includes(lower)
	);
};

const Handovers: React.FC = () => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const { loggedUserValue } = useLoggedUser();

	const [propertiesDropdownOpen, setPropertiesDropdownOpen] = useState(false);
	const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
	const [selectedStatusFilter, setSelectedStatusFilter] = useState<string[]>([]);

	const selectedPropertiesFilter = useGuideFiltersStore(state => state.filters.selectedPropertiesFilter);
	const setPropertyFilters = useGuideFiltersStore(state => state.setPropertyFilters);
	const searchTerm = useGuideFiltersStore(state => state.filters.searchGuideTerm);
	const setSearchTerm = useGuideFiltersStore(state => state.setSearchTerm);

	const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY_MS);

	useEffect(() => {
		setDebouncedSearchTerm(searchTerm);
	}, [searchTerm, setDebouncedSearchTerm]);

	const handleOnError = useCallback(
		(error: ApolloError) => {
			console.error("Error fetching handovers:", error);
		},
		[loggedUserValue.organization_id]
	);

	const handleOnComplete = useCallback(
		data => {
			console.log("Get Handover By OrganizationIds data: ", data);
		},
		[loggedUserValue]
	);

	const { data, loading } = useGetHandoverByOrganizationIdsQuery({
		skip: !loggedUserValue.organization_id,
		variables: { organization_ids: [loggedUserValue.organization_id] },
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnError,
		onCompleted: handleOnComplete
	});

	const groupedHandovers = useMemo<GroupedHandovers>(() => {
		const allHandovers = (data?.handover ?? []) as Handover[];

		const filtered = allHandovers.filter(handover => {
			const matchesStatus = selectedStatusFilter.length === 0 || selectedStatusFilter.includes(handover.status);
			const matchesProperty =
				selectedPropertiesFilter.length === 0 || selectedPropertiesFilter.includes(handover.property_id);
			const matchesText = matchesSearch(handover, debouncedSearchTerm);
			return matchesStatus && matchesProperty && matchesText;
		});

		const result: GroupedHandovers = { forReview: [], open: [], completed: [] };
		filtered.forEach(handover => {
			let isVendor = true;

			if (handover) {
				// Determine role: compare logged user's org against the handover's vendor org.
				isVendor = loggedUserValue.organization_id === handover.vendor_organization_id;
			} else {
				// Force a role perspective when no handover object is available
				isVendor = loggedUserValue.organization?.type === OrganizationType.Vendor;
			}

			if (COMPLETED_STATUSES.has(handover.status)) {
				result.completed.push(handover);
			} else {
				if (isVendor) {
					if (VENDOR_FOR_REVIEW_STATUSES.has(handover.status)) {
						result.forReview.push(handover);
					} else if (VENDOR_OPEN_STATUSES.has(handover.status)) {
						result.open.push(handover);
					}
				} else {
					if (CUSTOMER_FOR_REVIEW_STATUSES.has(handover.status)) {
						result.forReview.push(handover);
					} else if (CUSTOMER_OPEN_STATUSES.has(handover.status)) {
						result.open.push(handover);
					}
				}
			}
			// Unknown statuses are intentionally ignored rather than silently misclassified
		});

		return result;
	}, [data, selectedStatusFilter, selectedPropertiesFilter, debouncedSearchTerm]);

	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value),
		[setSearchTerm]
	);

	const handlePropertiesSelect = useCallback(
		({ selectedKeys }: { selectedKeys: string[] }) => {
			if (selectedKeys.includes("property-all")) {
				setPropertyFilters([]);
			} else {
				setPropertyFilters(selectedKeys);
			}
		},
		[setPropertyFilters]
	);

	const handleStatusSelect = useCallback(({ selectedKeys }: { selectedKeys: string[] }) => {
		if (selectedKeys.includes("status-all")) {
			setSelectedStatusFilter([]);
		} else {
			setSelectedStatusFilter(selectedKeys);
		}
	}, []);

	const [propertiesOptions, propertyDropdownLabel] = useMemo((): [MenuProps["items"], string] => {
		const userProperties = loggedUserValue?.user_properties;
		if (!userProperties?.length) return [[], "All Properties"];

		const allSelected =
			selectedPropertiesFilter.length === 0 || selectedPropertiesFilter.length === userProperties.length;
		let selectedName: string | undefined;

		const items: MenuProps["items"] = [
			{
				key: "property-all",
				icon: (
					<div>
						<input
							className="custom-checkbox"
							id="property-all"
							type="checkbox"
							checked={allSelected}
							onChange={voidFnc}
							onClick={e => e.stopPropagation()}
						/>
						<label htmlFor="property-all" className="custom-checkbox-label">
							All properties
						</label>
					</div>
				)
			},
			...userProperties.map((up, i) => {
				const property = up.property;
				const id = `property-${i}-${property?.id}`;
				const isSelected = selectedPropertiesFilter.includes(property?.id);
				if (!selectedName && isSelected) selectedName = property?.name;
				return {
					key: `${property?.id}`,
					icon: (
						<div>
							<input
								className="custom-checkbox"
								id={id}
								type="checkbox"
								checked={isSelected}
								onChange={voidFnc}
								onClick={e => e.stopPropagation()}
							/>
							<label htmlFor={id} className="custom-checkbox-label">
								{property?.name}
							</label>
						</div>
					)
				};
			})
		];

		return [items, createLabelForFilters("All Properties", selectedPropertiesFilter, selectedName)];
	}, [selectedPropertiesFilter, loggedUserValue.user_properties]);

	const [statusOptions, statusDropdownLabel] = useMemo((): [MenuProps["items"], string] => {
		const allSelected =
			selectedStatusFilter.length === 0 || selectedStatusFilter.length === HandoverStatusValues.length;
		let selectedName: string | undefined;

		const items: MenuProps["items"] = [
			{
				key: "status-all",
				icon: (
					<div>
						<input
							className="custom-checkbox"
							id="status-all"
							type="checkbox"
							checked={allSelected}
							onChange={voidFnc}
							onClick={e => e.stopPropagation()}
						/>
						<label htmlFor="status-all" className="custom-checkbox-label">
							All statuses
						</label>
					</div>
				)
			},
			...HandoverStatusValues.map((status, i) => {
				const id = `status-${i}-${status}`;
				const isSelected = selectedStatusFilter.includes(status);
				if (!selectedName && isSelected) selectedName = status;
				return {
					key: status,
					icon: (
						<div>
							<input
								className="custom-checkbox"
								id={id}
								type="checkbox"
								checked={isSelected}
								onChange={voidFnc}
								onClick={e => e.stopPropagation()}
							/>
							<label htmlFor={id} className="custom-checkbox-label">
								{status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " ")}
							</label>
						</div>
					)
				};
			})
		];

		return [items, createLabelForFilters("Status", selectedStatusFilter, selectedName)];
	}, [selectedStatusFilter]);

	const { forReview, open, completed } = groupedHandovers;

	return (
		<div className="products-screen-container">
			<span className="handovers-title">My Handovers</span>
			<div className="handovers-content-container" ref={scrollContainerRef}>
				{/* Filters */}
				<div className="handovers-filter-group">
					<div className="search-products-row">
						<div className="products-search-container">
							<Input
								className="product-search-input-wrapper"
								name="searchHandover"
								placeholder="Search..."
								classNames={{ input: "product-search-input" }}
								value={searchTerm}
								autoComplete="off"
								onChange={handleSearchChange}
								prefix={<HiSearch color="#67748E" size="16px" />}
								allowClear
							/>
						</div>
					</div>
					<div className="products-filters-and-create-guide-row">
						<div className="products-filters-row">
							<Dropdown
								menu={{
									items: propertiesOptions,
									selectable: true,
									multiple: true,
									onSelect: handlePropertiesSelect,
									onDeselect: handlePropertiesSelect,
									selectedKeys: selectedPropertiesFilter
								}}
								arrow
								className="products-dropdown"
								overlayClassName="products-dropdown-overlay"
								trigger={["click"]}
								open={propertiesDropdownOpen}
								onOpenChange={(open, info) => {
									if (info.source === "trigger" && !open) setPropertiesDropdownOpen(false);
								}}
								disabled={!propertiesOptions?.length}
							>
								<Button onClick={() => setPropertiesDropdownOpen(prev => !prev)}>
									{propertyDropdownLabel}
									<img
										src={`${process.env.PUBLIC_URL}/assets/images/icons/black-chevron-down.svg`}
										style={{ width: "1em", height: "1em" }}
										className="chevron-button"
										alt=""
									/>
								</Button>
							</Dropdown>

							<Dropdown
								menu={{
									items: statusOptions,
									selectable: true,
									multiple: true,
									onSelect: handleStatusSelect,
									onDeselect: handleStatusSelect,
									selectedKeys: selectedStatusFilter
								}}
								arrow
								className="products-dropdown"
								trigger={["click"]}
								open={statusDropdownOpen}
								onOpenChange={(open, info) => {
									if (info.source === "trigger" && !open) setStatusDropdownOpen(false);
								}}
								disabled={!statusOptions?.length}
							>
								<Button onClick={() => setStatusDropdownOpen(prev => !prev)}>
									{statusDropdownLabel}
									<img
										src={`${process.env.PUBLIC_URL}/assets/images/icons/black-chevron-down.svg`}
										style={{ width: "1em", height: "1em" }}
										className="chevron-button"
										alt=""
									/>
								</Button>
							</Dropdown>
						</div>

						<Link to={routeNames.HANDOVERS_ADD} className="highnote-button" style={{ gap: "8px" }}>
							<img src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-white.svg`} alt="" />
							New handover
						</Link>
					</div>
				</div>

				{/* Handover groups */}
				<div className="handovers-list-container">
					{forReview.length > 0 && (
						<section>
							<div className="handovers-status-header">For review</div>
							<div className="handovers-section-list">
								{forReview.map(handover => (
									<HandoversListItem key={handover?.id} handover={handover} isForReview />
								))}
							</div>
						</section>
					)}

					{open.length > 0 && (
						<section>
							<div className="handovers-status-header">Open</div>
							<div className="handovers-section-list">
								{open.map(handover => (
									<HandoversListItem key={handover?.id} handover={handover} />
								))}
							</div>
						</section>
					)}

					{completed.length > 0 && (
						<section>
							<div className="handovers-status-header">Completed</div>
							<div className="handovers-section-list">
								{completed.map(handover => (
									<HandoversListItem key={handover?.id} handover={handover} />
								))}
							</div>
						</section>
					)}

					{!loading && forReview.length === 0 && open.length === 0 && completed.length === 0 && (
						<div className="handovers-empty">
							Handovers will display here, organized by For Review, Open, and Completed.
						</div>
					)}
				</div>

				<ScrollToTopButton parentRef={scrollContainerRef} />
			</div>
		</div>
	);
};

export default Handovers;
